import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import { Screen, Header } from "@/components/Layout";
import { Spinner } from "@/components/Controls";
import { colors, spacing, radius, fonts, type } from "@/theme";
import { getStoredUserId, fetchSummaries, fetchUser } from "@/api";

const MOODS = {
  cheerful: { label: "Cheerful", icon: "sun", color: "#3F6844" },
  content: { label: "Content", icon: "smile", color: "#3F6844" },
  okay: { label: "Doing okay", icon: "meh", color: "#996300" },
  reflective: { label: "Reflective", icon: "cloud", color: "#7A6656" },
  low: { label: "A little low", icon: "cloud-drizzle", color: "#A63C3C" },
};

function when(iso) {
  const d = new Date(iso);
  const now = new Date();
  const days = Math.floor((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (days <= 0) return `Today · ${time}`;
  if (days === 1) return `Yesterday · ${time}`;
  return `${d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · ${time}`;
}

export default function Family() {
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState([]);
  const [name, setName] = useState("");
  const [shareOn, setShareOn] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const id = getStoredUserId();
      if (!id) {
        navigate("/onboarding", { replace: true });
        return;
      }
      const [items, profile] = await Promise.all([fetchSummaries(id), fetchUser(id).catch(() => null)]);
      setSummaries(items);
      if (profile) {
        setName(profile.user.first_name);
        setShareOn(!!profile.user.share_summaries);
      }
      setLoading(false);
    })();
  }, [navigate]);

  return (
    <Screen testID="family-screen">
      <Header title="Family updates" testID="family-back" />
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: spacing["3xl"] }}><Spinner size={40} /></div>
      ) : (
        <div style={{ padding: `${spacing.xl}px ${spacing.lg}px ${spacing["2xl"]}px` }}>
          <p style={{ ...fonts.text, fontSize: type.lg, color: colors.muted, lineHeight: 1.5, marginBottom: spacing.xl }}>
            {shareOn ? `A warm note after each of ${name || "your loved one"}'s calls, so you feel close by.` : "Sharing is currently off. Turn on \u201CShare call summaries\u201D in Settings to receive updates here."}
          </p>

          {summaries.length > 0 && (
            <div data-testid="mood-trend" style={{ background: colors.surfaceSecondary, borderRadius: radius.lg, padding: spacing.lg, marginBottom: spacing.xl }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: spacing.md }}>
                <span style={{ ...fonts.textBold, fontSize: type.lg, color: colors.onSurface }}>Recent moods</span>
                <span style={{ ...fonts.text, fontSize: type.base, color: colors.muted }}>last {Math.min(7, summaries.length)} calls</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {summaries.slice(0, 7).reverse().map((s) => {
                  const mood = MOODS[s.mood] ?? MOODS.okay;
                  return (
                    <div key={s.id} data-testid={`trend-dot-${s.id}`} style={{ width: 40, height: 40, borderRadius: 20, background: mood.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon name={mood.icon} size={16} color={colors.onBrandPrimary} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {summaries.length === 0 ? (
            <div data-testid="family-empty" style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: spacing["3xl"], gap: spacing.lg }}>
              <Icon name="mail" size={48} color={colors.border} />
              <div style={{ ...fonts.textSemiBold, fontSize: type.lg, color: colors.muted, textAlign: "center", padding: `0 ${spacing.xl}px`, lineHeight: 1.5 }}>
                No updates yet. After the next call ends, a gentle summary will appear here.
              </div>
            </div>
          ) : (
            summaries.map((s) => {
              const mood = MOODS[s.mood] ?? MOODS.okay;
              return (
                <div key={s.id} data-testid={`summary-${s.id}`} style={{ background: colors.surfaceSecondary, borderRadius: radius.lg, padding: spacing.lg, marginBottom: spacing.md }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: spacing.sm }}>
                    <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.brand }}>{when(s.created_at)}</span>
                    <div data-testid={`summary-mood-${s.id}`} style={{ display: "flex", alignItems: "center", gap: spacing.xs, background: mood.color, borderRadius: radius.pill, padding: "4px 8px" }}>
                      <Icon name={mood.icon} size={14} color={colors.onBrandPrimary} />
                      <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.onBrandPrimary }}>{mood.label}</span>
                    </div>
                  </div>
                  <div style={{ ...fonts.textSemiBold, fontSize: type.lg, color: colors.onSurface, lineHeight: 1.4 }}>{s.summary}</div>
                </div>
              );
            })
          )}
        </div>
      )}
    </Screen>
  );
}
